import { useQuery, useMutation } from '@tanstack/react-query';
import { getDepartmentsList, getIndentList, GetIndentListParams, MultipleConvertToPRIndents, MultipleConvertToPRIndentsPayload, ForceCloseIndentsResponse, forwardIndents, ForwardIndentsPayload, getActiveDepartmentsList } from '../StockTransfer/stockTransfer.helper';
import { CreateIssuePayload, getIssuedList, getIssueProspectiveList, IndentToCreateIssue, CreateIssueResponse, getShowStock } from '../StockTransfer/Issue/Issue.helper';
import { CreateReceive, CreateReceivePayload, CreateReceiveResponse, getReceivedList, getReceiveProspectiveList } from '../StockTransfer/Receive/Receive.helper';



//get clinic list
export const useIndentList = (params: GetIndentListParams = {}) => {
  // Strip any client-only fields (e.g., _applyTrigger) before calling API
  const apiParams = { ...params } as Record<string, unknown>;
  delete apiParams._applyTrigger;
  return useQuery({
    queryKey: ['IndentList', params],  // dynamic caching by params (including trigger)
    queryFn: () => getIndentList(apiParams as GetIndentListParams),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 2,       // cache 2 min
  });
};

export const useDepartmentList = () => {
  return useQuery({
    queryKey: ['DepartmentList'],  // dynamic caching by params
    queryFn: () => getDepartmentsList(),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 2,       // cache 2 min
  });
};

export const useActiveDepartmentList = (itemId: number) => {
  return useQuery({
    queryKey: ['ActiveDepartmentList', itemId],  // dynamic caching by params
    queryFn: () => getActiveDepartmentsList(itemId),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 2,       // cache 2 min
  });
};

////////////////////////////// Issue module ////////////////////////////////
export const useIssueProspectiveList = (params: GetIndentListParams = {}) => {
  const apiParams = { ...params } as Record<string, unknown>;
  return useQuery({
    queryKey: ['ProspectiveList', params],  
    queryFn: () => getIssueProspectiveList(apiParams as GetIndentListParams),
    retry: 1, // 1 retry on fail
    // staleTime: 1000 * 60 * 2,       // cache 2 min
  });
};



export const useIssuedList = (params: GetIndentListParams = {}) => {
  const apiParams = { ...params } as Record<string, unknown>;
  return useQuery({
    queryKey: ['IssuedList', params],  
    queryFn: () => getIssuedList(apiParams as GetIndentListParams),
    retry: 1, // 1 retry on fail
    // staleTime: 1000 * 60 * 2,       // cache 2 min
  });
};

// Bulk convert to PR (POST)
export const useBulkConvertToPr = () => {
  return useMutation<ForceCloseIndentsResponse, unknown, MultipleConvertToPRIndentsPayload>({
    mutationKey: ['BulkConvertToPR'],
    mutationFn: (payload) => MultipleConvertToPRIndents(payload),
  });
};

// Forward a single indent (POST)
export const useForwardIndent = () => {
  return useMutation<ForceCloseIndentsResponse, unknown, { id: number; data: ForwardIndentsPayload }>({
    mutationKey: ['ForwardIndent'],
    mutationFn: ({ id, data }) => forwardIndents(id, data),
  });
};

// Create single issue
export const useCreateIssue = () => {
  return useMutation<CreateIssueResponse, unknown, { id: number; data: CreateIssuePayload }>({
    mutationKey: ['CreateIssue'],
    mutationFn: ({ id, data }) => IndentToCreateIssue(id, data),
  });
};

// Get stock batch information for items
export const useShowStock = (itemIds: number | number[] | undefined, fromStore?: number) => {
  return useQuery({
    queryKey: ['ShowStockData', itemIds, fromStore],
    queryFn: () => getShowStock({ itemIds: itemIds!, from_store: fromStore }),
    enabled: !!itemIds && (Array.isArray(itemIds) ? itemIds.length > 0 : true),
    retry: 1,
    staleTime: 1000 * 60 * 1, // cache 1 min
  });
};


////////////////////////////// Receive module ////////////////////////////////

export const useReceiveProspectiveList = (params: GetIndentListParams = {}) => {
  const apiParams = { ...params } as Record<string, unknown>;
  return useQuery({
    queryKey: ['ReceiveProspectiveList', params],  
    queryFn: () => getReceiveProspectiveList(apiParams as GetIndentListParams),
    retry: 1, // 1 retry on fail
    // staleTime: 1000 * 60 * 1,       // cache 1 min
  });
};


export const useReceivedList = (params: GetIndentListParams = {}) => {
  const apiParams = { ...params } as Record<string, unknown>;
  return useQuery({
    queryKey: ['ReceivedList', params],  
    queryFn: () => getReceivedList(apiParams as GetIndentListParams),
    retry: 1, // 1 retry on fail
    // staleTime: 1000 * 60 * 2,       // cache 2 min
  });
};

// Create single Receive
export const useCreateReceive = () => {
  return useMutation<CreateReceiveResponse, unknown, { id: number; data: CreateReceivePayload }>({
    mutationKey: ['CreateReceive'],
    mutationFn: ({ id, data }) => CreateReceive(id, data),
  });
};